import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import redeem_services from "services/redeem_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import type { RedeemRequest } from "types/redeem";

export type RedeemLookupStatus =
  | "idle" // no lookup is running (disabled by the caller, or no auth yet)
  | "loading" // a request is in flight for the current request id
  | "found" // the chain returned a matching redeem
  | "not-found" // the chain answered, definitively, with no such redeem
  | "error"; // the lookup could not be completed (network/server error)

export interface UseRedeemLookupResult {
  status: RedeemLookupStatus;
  request: RedeemRequest | null;
  /** Re-run the lookup — used to recover from the `error` state. */
  refetch: () => void;
}

/**
 * Looks a redeem request up on-chain — the fallback used when it isn't among the
 * recent redeems already loaded. Gated by `enabled` so the request only fires
 * when the caller has decided a lookup is warranted (e.g. the local list came up
 * empty for a complete request id).
 *
 * Crucially, a request that fails (network/5xx) surfaces as `error`, never as
 * `not-found` — only a definitive answer with no redeem is a true miss, so the
 * UI never tells the operator a redeem doesn't exist when the lookup simply
 * couldn't reach the chain.
 */
export function useRedeemLookup(
  requestId: string,
  enabled: boolean,
): UseRedeemLookupResult {
  const { user } = useAuth();
  const networkType = useSelector(networkTypeSelector);
  const normalized = requestId.trim();
  const active = !!user && enabled && normalized.length > 0;

  // Redeem ids are only unique per network, so the network is part of the key —
  // otherwise a testnet hit would be served for the same id on mainnet.
  const query = useQuery<RedeemRequest | null>({
    queryKey: ["redeem-lookup", networkType, normalized],
    queryFn: () => redeem_services.lookupRedeem(normalized, networkType),
    enabled: active,
    retry: false,
    staleTime: 60_000,
  });

  const request = active ? (query.data ?? null) : null;

  let status: RedeemLookupStatus;
  if (!active) {
    status = "idle";
  } else if (query.isFetching) {
    status = "loading";
  } else if (query.isError) {
    status = "error";
  } else if (query.isSuccess) {
    status = request ? "found" : "not-found";
  } else {
    // Active but not yet fetching — the tick between enabling and the request
    // actually starting. Treat as loading so we never flash a stale outcome.
    status = "loading";
  }

  return { status, request, refetch: query.refetch };
}
